/**
 * Handlers for ADF `mediaSingle` / `media` nodes.
 *
 * Strategy: produce a CommonMark image (`![alt](url)`) so the markdown is
 * portable to external clients (VS Code preview, GitHub, etc.). What goes
 * in `url` depends on the media node's `type`:
 *
 *   - `external` — emit `attrs.url` verbatim. No auth involved.
 *   - `file`     — emit a tenant-scoped Confluence proxy URL
 *                  (`/wiki/download/attachments/{contentId}/{filename}`)
 *                  produced by an injected `serializeFileMedia` callback.
 *                  The proxy resolves with the viewer's Confluence session
 *                  cookie, so no media token / PAT ever lives in the
 *                  markdown text. When no callback is supplied (e.g. the
 *                  default platform handler used outside Confluence) we
 *                  fall back to alt-text-only — never a `blob:` / token
 *                  URL — to keep this safe by default.
 *
 * Caveats deliberately accepted in v1:
 *   - `mediaSingle` layout / width are dropped on serialise. CommonMark
 *     has no representation for them and the round-trip parser
 *     (`@atlaskit/editor-markdown-transformer` + `markdown-it-media`)
 *     re-applies schema defaults on the way back, so the editor will
 *     re-render them sensibly.
 *   - A `mediaSingle` caption is dropped: only its first `media` child is
 *     serialised. `mediaGroup` and `mediaInline` have no default handler:
 *     nested ones are skipped and a top-level `mediaGroup` throws, so
 *     filter them out before serialising.
 */

import type { Image, Paragraph } from 'mdast';

import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

import type { FromPMHandlers } from '../types';
import { readString } from '../utils';

/**
 * Callback that converts a `media (type: "file")` node's attributes into a
 * URL the viewer can fetch. Implementations should produce a real,
 * authenticated-via-cookie URL (e.g. Confluence's
 * `/wiki/download/attachments/{contentId}/{filename}` proxy) — never a
 * media token or signed download URL, since the markdown text is shared
 * (collab, exports) and tokens would leak.
 *
 * Returning `undefined` (or omitting the callback entirely) causes the
 * media to be serialised as alt-text-only, which preserves the user's
 * intent without producing a broken `<img src="">`.
 */
export type SerializeFileMedia = (attrs: {
	alt?: string;
	collection?: string;
	height?: number;
	id: string;
	width?: number;
}) => string | undefined;

const readNumber = (value: unknown): number | undefined =>
	typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/**
 * Convert a single `media` node to an mdast `image`. Returns `undefined`
 * when the node has no representable URL — the caller will skip it.
 */
const mediaToImage = (node: PMNode, serializeFileMedia?: SerializeFileMedia): Image | undefined => {
	const attrs = node.attrs ?? {};
	const type = readString(attrs.type);
	const alt = readString(attrs.alt) ?? '';

	let url: string | undefined;

	if (type === 'external') {
		// External media: URL is public and lives on `attrs.url`.
		url = readString(attrs.url);
	} else if (type === 'file') {
		// File media: rely on the Confluence-supplied proxy serialiser.
		// Without one, we have no way to express the bytes in valid markdown
		// (we deliberately refuse to embed media tokens or fake `blob:` URLs).
		const id = readString(attrs.id);
		if (id && serializeFileMedia) {
			url = serializeFileMedia({
				id,
				collection: readString(attrs.collection),
				alt: readString(attrs.alt),
				width: readNumber(attrs.width),
				height: readNumber(attrs.height),
			});
		}
	} else {
		// Unknown / future media types — best-effort: pick `url` if present,
		// otherwise drop. This keeps us forward-compatible without inventing
		// behaviour we can't reason about.
		url = readString(attrs.url);
	}

	if (!url) {
		// No URL available, e.g. `file` media with no `serializeFileMedia`
		// result. Keep the alt text as `![alt]()` rather than throwing or
		// silently dropping it; with no alt text there is nothing to emit.
		if (!alt) {
			return undefined;
		}
		return { type: 'image', url: '', alt };
	}

	return { type: 'image', url, alt };
};

/**
 * Wrap an `Image` (mdast PhrasingContent — inline) in a `Paragraph` so it
 * can be inserted as a block-level child of the mdast root.
 *
 * Why: ADF's `mediaSingle` is a block-level node (sibling of `paragraph` /
 * `heading`), but CommonMark has no block-level image construct. The
 * closest valid representation is a paragraph containing only the image,
 * which `mdast-util-to-markdown` will surround with blank lines — exactly
 * the visual block effect we want.
 *
 * Skipping this wrapper causes `mdast-util-to-markdown` to concatenate
 * the inline image with neighbouring blocks (and to drop the blank-line
 * separators), which manifests as the entire document compressing onto
 * one line — see EDITOR-6636.
 */
const wrapInParagraph = (image: Image | undefined): Paragraph | undefined =>
	image ? { type: 'paragraph', children: [image] } : undefined;

/**
 * Default `media` handler — used when no `serializeFileMedia` is provided.
 * `file`-type media is dropped; `external` media is emitted as a CommonMark
 * image wrapped in a paragraph (see `wrapInParagraph`).
 *
 * In practice the editor schema only allows `media` inside `mediaSingle`,
 * `mediaGroup`, etc., so this top-level handler is a safety net for
 * documents where a bare `media` somehow reaches the root. Production
 * traffic almost always hits `mediaSingle` instead.
 */
export const media = (node: PMNode): Paragraph | undefined => wrapInParagraph(mediaToImage(node));

/**
 * Default `mediaSingle` handler — recurses into the wrapped media node and
 * returns a paragraph containing the resulting CommonMark image. Layout /
 * width attrs on the `mediaSingle` are dropped (no CommonMark equivalent).
 */
export const mediaSingle = (node: PMNode): Paragraph | undefined => {
	let mediaChild: PMNode | undefined;
	node.forEach((child) => {
		if (!mediaChild && child.type.name === 'media') {
			mediaChild = child;
		}
	});
	if (!mediaChild) {
		return undefined;
	}
	return wrapInParagraph(mediaToImage(mediaChild));
};

/**
 * Build a pair of `media` / `mediaSingle` handlers that delegate to a
 * caller-supplied `serializeFileMedia` for `type: "file"` media. Compose
 * the result into your `FromPMHandlers` map:
 *
 * ```ts
 * import { fromPMHandlers } from '@af/mdast-util-from-pm/handlers';
 * import { createMediaHandlers } from '@af/mdast-util-from-pm/handlers/media';
 *
 * const handlers = {
 *   ...fromPMHandlers,
 *   ...createMediaHandlers({ serializeFileMedia: makeProxyUrl }),
 * };
 * ```
 */
export const createMediaHandlers = ({
	serializeFileMedia,
}: {
	serializeFileMedia?: SerializeFileMedia;
} = {}): Pick<FromPMHandlers, 'media' | 'mediaSingle'> => ({
	media: (node: PMNode) => wrapInParagraph(mediaToImage(node, serializeFileMedia)),
	mediaSingle: (node: PMNode) => {
		let mediaChild: PMNode | undefined;
		node.forEach((child) => {
			if (!mediaChild && child.type.name === 'media') {
				mediaChild = child;
			}
		});
		return mediaChild ? wrapInParagraph(mediaToImage(mediaChild, serializeFileMedia)) : undefined;
	},
});
